import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useConversionStatusBatch } from '@/console/features/projects/components/conversion-status-batch/conversion-status-batch-context';
import { TUseConversionStatusProps } from '@/console/features/projects/hooks/use-conversion-status/use-conversion-status.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import {
  useCreateConversionMutation,
  useReadJobsByProjectIdQuery,
} from '@/shared/redux/api/jobs/jobs-api';
import {
  FeatureEnum,
  JobStatusEnum,
  ReadJobsByProjectIdQuery,
} from '@/shared/types/types.generated';

const CONVERSION_STATUS_POLLING_INTERVAL_MS = 30_000;

export const useConversionStatus = ({
  versionId,
  projectId,
  disabled = false,
}: TUseConversionStatusProps) => {
  const { t } = useTranslation();
  const batchContext = useConversionStatusBatch();
  const useBatch = !!batchContext;

  const [createConversion, { isLoading: isCreateConversionLoading }] =
    useCreateConversionMutation();
  const { isJobRunning } = useJobStatus();

  const shouldStopPolling = (data: ReadJobsByProjectIdQuery) => {
    const status = data?.readJobsByProjectId.items[0]?.status;
    return !isJobRunning(status);
  };

  const {
    data,
    isLoading: isQueryLoading,
    isError,
    error,
  } = usePollingQuery({
    useQueryHook: useReadJobsByProjectIdQuery,
    queryArg: {
      projectId: projectId ?? '',
      jobTypes: [FeatureEnum.ProjectConvert],
      filter: versionId
        ? { projectVersionIds: { containsAny: [versionId] } }
        : undefined,
      pagination: { page: 1, pageSize: 2 },
    },
    shouldStopPolling,
    pollingInterval: CONVERSION_STATUS_POLLING_INTERVAL_MS,
    queryOptions: {
      skip: !versionId || !projectId || disabled || useBatch,
    },
    enableLogging: true,
  });

  const isLoading = useBatch ? batchContext.isLoading : isQueryLoading;

  const items = useMemo(() => {
    if (useBatch && versionId) {
      return batchContext.getJobsForVersion(versionId);
    }
    return data?.readJobsByProjectId.items ?? [];
  }, [useBatch, versionId, batchContext, data]);

  const latestJob = items[0];
  const previousJob = items[1];

  const getJob = () => {
    if (
      previousJob &&
      (latestJob?.status === JobStatusEnum.Processing ||
        latestJob?.status === JobStatusEnum.Pending)
    ) {
      return previousJob;
    }

    return latestJob;
  };

  const job = getJob();

  const jobId = job?.jobId;
  const status = job?.status;
  const errorMessage = useMemo(() => job?.errorMessage, [job?.errorMessage]);

  const getStatusTooltip = () => {
    if (isJobRunning(status)) {
      return t('projects:conversion_in_progress');
    }

    if (isLoading) {
      return t('projects:loading_conversion_status');
    }

    return t('projects:conversion_not_successful');
  };

  const triggerConversion = async (
    targetProjectId: string,
    targetVersionId: string,
  ) => {
    try {
      await createConversion({
        projectId: targetProjectId,
        versionId: targetVersionId,
      }).unwrap();
      toast.success(t('pipelines:trigger_conversion_success'));
    } catch (error) {
      console.error(error);
      toast.error(t('pipelines:trigger_conversion_error'));
    }
  };

  useEffect(() => {
    if (errorMessage) {
      console.error(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  return {
    job,
    jobId,
    status,
    isLoading,
    isCreateConversionLoading,
    errorMessage,
    isError,
    getStatusTooltip,
    triggerConversion,
  };
};
